import React, { useEffect, useState } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { HiOutlineLocationMarker } from 'react-icons/hi';

import { useUiComponentsInternationalContext } from '@repo/static-translations/src';
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@repo/ui-components/shadcn/Form';
import { Input } from '@repo/ui-components/shadcn/Input';
import { fetchAddressSuggestions } from './api';
import { pushToDataLayer } from './utils';

interface AddressFieldProps {
  form: UseFormReturn<{
    firstName?: string;
    lastName?: string;
    email?: string;
    phoneNumber?: string;
    streetAddress?: string;
    postalCode?: string;
    referralCode?: string;
  }>;
  handlePersonalDetailsChange: (name: string, value: string) => void;
  debounceDelay?: number;
}

interface Suggestion {
  address: string;
  id: string;
  place_name: string;
  context: { id: string; text: string; short_code?: string }[];
  text: string;
}

const AddressField: React.FC<AddressFieldProps> = ({
  form,
  handlePersonalDetailsChange,
  debounceDelay = 200,
}) => {
  const [addressSuggestions, setAddressSuggestions] = useState<Suggestion[]>(
    []
  );
  const [query, setQuery] = useState('');

  const { tt } = useUiComponentsInternationalContext();

  const setPostalCode = (postalCode: string) => {
    form.setValue('postalCode', postalCode);
  };

  useEffect(() => {
    if (query) {
      const handler = setTimeout(async () => {
        try {
          // reset address field validation
          form.clearErrors('streetAddress');
          // Attempt to fetch suggestions
          const suggestions = await fetchAddressSuggestions(query);
          setAddressSuggestions(suggestions);

          // let analytics know if no suggestions are returned
          if (suggestions.length === 0) {
            pushToDataLayer({
              event: 'prospect_form_address_geocoding_fail',
              address_string: query,
            });
          }
        } catch (error) {
          // If the fetch fails, clear suggestions and set an error message
          setAddressSuggestions([]);
          // invalidate address field
          form.setError('streetAddress', {
            type: 'manual',
            message: 'Address service is currently unavailable',
          });
        }
      }, debounceDelay);

      return () => clearTimeout(handler);
    }
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, debounceDelay]);

  const handleAddressChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    field: { onChange: (...event: any[]) => void; name: string }
  ) => {
    field.onChange(e);
    setPostalCode('');
    handlePersonalDetailsChange('postalCode', '');
    setQuery(e.target.value);
    handlePersonalDetailsChange(e.target.name, e.target.value);
  };

  const handleSuggestionClick = (suggestion: Suggestion) => {
    form.setValue('streetAddress', suggestion.place_name);
    const streetAddress = suggestion.address ? `${suggestion.address} ` : '';
    handlePersonalDetailsChange(
      'streetAddress',
      streetAddress + (suggestion.text || '')
    );

    const city =
      suggestion.context.find(context => context.id.includes('place'))?.text ||
      '';
    handlePersonalDetailsChange('city', city);

    const stateAbbr =
      suggestion.context.find(context => context.id.includes('region'))
        ?.short_code || '';
    // chop off the US- or CA- prefix for API call
    const stateAbbrWithoutPrefix = stateAbbr.replace(/^(US|CA)-/, '');
    handlePersonalDetailsChange('stateAbbr', stateAbbrWithoutPrefix);

    const country =
      suggestion.context.find(context => context.id.includes('country'))
        ?.text || '';
    handlePersonalDetailsChange('country', country);

    const postalCode =
      suggestion.context.find(context => context.id.includes('postcode'))
        ?.text || '';
    form.setValue('postalCode', postalCode);
    handlePersonalDetailsChange('postalCode', postalCode);

    setAddressSuggestions([]);
  };

  const renderSuggestions = () => {
    if (addressSuggestions.length === 0) return null;

    return (
      <ul className="absolute top-16  bg-white border border-t-0 z-10 list-none text-sm p-0 rounded-b-md shadow-lg w-full">
        {addressSuggestions.map(suggestion => (
          <li
            className="border border-b border-dashed last:border-none"
            key={suggestion.id}
          >
            <button
              type="button"
              className="p-2 cursor-pointer text-left w-full overflow-hidden text-ellipsis hover:bg-gray-100"
              onClick={() => handleSuggestionClick(suggestion)}
            >
              {suggestion.place_name}
            </button>
          </li>
        ))}
      </ul>
    );
  };

  return (
    <>
      <FormField
        control={form.control}
        name="streetAddress"
        render={({ field }) => (
          <FormItem className="w-full relative">
            <FormLabel>
              {tt(
                s => s.blockBuilderProspectSchedulerComponent.streetAddressLabel
              )}
            </FormLabel>
            <FormControl>
              <Input
                autoComplete="off"
                iconComponent={<HiOutlineLocationMarker size="22" />}
                hasError={!!form.formState.errors.streetAddress}
                {...field}
                onChange={e => handleAddressChange(e, field)}
                required
              />
            </FormControl>
            {renderSuggestions()}
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name="postalCode"
        render={({ field }) => (
          <FormControl>
            <div className="hidden">
              <Input
                className="hidden"
                type="hidden"
                {...field}
                onChange={e => {
                  field.onChange(e);
                  handlePersonalDetailsChange(e.target.name, e.target.value);
                }}
                required
              />
            </div>
          </FormControl>
        )}
      />
    </>
  );
};

export default AddressField;
